/**
 * Pure helpers for the `chart` core view (ported from the MEIS-13490
 * prototype's `agentChartSpec.ts`): validating a spec that came in over the
 * wire before handing it to the chart component. What the assistant
 * remembers about a shown chart is the backend's `view.memory`. Number formatting for
 * display lives in `AgentChartView` via `useFormatting()` — this file stays
 * free of locale concerns.
 */
import type { AgentViewSpec } from "./registry.js";

export type ChartSeries = {
  label: string;
  data: number[];
};

export type ChartViewSpec = {
  component: "chart";
  title: string;
  /** Optional one-line caption shown below the title (`null` when absent). */
  description?: string | null;
  chartType: "bar" | "pie" | "line";
  /** x-axis labels (bar/line) or slice labels (pie); 1..24 entries. */
  categories: string[];
  /** bar/line: 1..4 series; pie: exactly 1. */
  series: ChartSeries[];
  /** e.g. "€", "%", "Templates"; shown in tooltips and the value axis (`null` when absent). */
  unit?: string | null;
  /** bar only. */
  stacked?: boolean;
};

const MAX_CATEGORIES = 24;
const MAX_SERIES = 4;
const CHART_TYPES: readonly string[] = ["bar", "pie", "line"];

function isUnknownRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * Every problem with the spec, so a malformed view can be reported at once
 * instead of crashing on the first bad access. Empty means the spec is safe
 * to render. `view` is the loosely typed value that came in over the wire
 * (`AgentViewSpec`) — not yet trusted to be a `ChartViewSpec`.
 */
export function validateChartView(view: AgentViewSpec): string[] {
  const problems: string[] = [];

  // Rendered as React children and branched on by `AgentChartView`: an
  // object here would crash the message, an unknown type render nothing.
  if (typeof view.title !== "string" || view.title.trim() === "")
    problems.push("title: must be a non-empty string");
  if (
    typeof view.chartType !== "string" ||
    !CHART_TYPES.includes(view.chartType)
  )
    problems.push('chartType: expected "bar", "line" or "pie"');
  // `null` counts as absent: the backend's `show_chart` sends a missing
  // description/unit as `null` (`agentViews.ts`).
  if (view.description != null && typeof view.description !== "string")
    problems.push("description: must be a string");
  if (view.unit != null && typeof view.unit !== "string")
    problems.push("unit: must be a string");

  const categories = view.categories;
  const categoriesOk =
    Array.isArray(categories) &&
    categories.length >= 1 &&
    categories.length <= MAX_CATEGORIES;
  if (!categoriesOk)
    problems.push(
      `categories: expected 1 to ${MAX_CATEGORIES} entries, got ${
        Array.isArray(categories) ? categories.length : 0
      }`
    );
  const categoryCount = Array.isArray(categories) ? categories.length : 0;

  const series = view.series;
  if (!Array.isArray(series) || series.length < 1) {
    problems.push("series: expected at least 1 series");
    return problems;
  }

  if (view.chartType === "pie" && series.length !== 1)
    problems.push(`pie chart: expected exactly 1 series, got ${series.length}`);
  else if (view.chartType !== "pie" && series.length > MAX_SERIES)
    problems.push(
      `expected at most ${MAX_SERIES} series, got ${series.length}`
    );

  series.forEach((rawSeries, index) => {
    const position = index + 1;
    if (!isUnknownRecord(rawSeries)) {
      problems.push(`series #${position}: not an object`);
      return;
    }
    const label =
      typeof rawSeries.label === "string" ? rawSeries.label : `#${position}`;
    const data = rawSeries.data;
    if (!Array.isArray(data) || data.length !== categoryCount) {
      problems.push(
        `series "${label}": length does not match the number of categories`
      );
      return;
    }
    if (
      data.some(value => typeof value !== "number" || !Number.isFinite(value))
    )
      problems.push(`series "${label}": contains a non-finite value`);
  });

  return problems;
}

/** Whether the spec is safe to hand to the chart components as-is. */
export function isChartViewValid(view: AgentViewSpec): boolean {
  return validateChartView(view).length === 0;
}
